/**
 * CreateTenantDialog Component
 *
 * Modal form for provisioning a new customer tenant (ZITADEL org). Collects every
 * field the `tmctl create` CLI collects — org core, CRM/subscription metadata,
 * optional limits, and the first admin user — and submits them to
 * `POST /api/tenant-management/orgs`.
 *
 * Gated by the caller's `canManageTenants` authority at the call site (the Create
 * button only renders for capable staff); the backend re-enforces it.
 *
 * @module components/CreateTenantDialog
 */

import { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Alert,
  Box,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import type { CreateTenantRequest } from '../types';
import { SUBSCRIPTION_STATUSES } from '../types';
import type { TenantsApiClient } from '../api/TenantsApiClient';
import { tenantsClient as defaultTenantsClient } from '../api/registry';
import { useOrgsStore } from '../stores/useOrgsStore';
import { useUsersStore } from '../stores/useUsersStore';
import { isValidEmail } from '../utils/email';
import { formatErrorMessage } from '../utils';
import { isHttpError } from '../api/http';

// =============================================================================
// Types
// =============================================================================

export interface CreateTenantDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called to close (cancel or after a successful create). */
  onClose: () => void;
  /** Called after a successful create, with a confirmation message. */
  onSuccess?: (message: string) => void;
  /** Injectable client for testing (defaults to the registry singleton). */
  client?: TenantsApiClient;
}

// =============================================================================
// Component
// =============================================================================

const DEFAULT_STATUS = 'trialing';

/**
 * Numeric field parsing: an empty string means "not set" → 0 (the backend's
 * omit sentinel). Non-numeric input is treated as 0 as well.
 */
function toCount(raw: string): number {
  const n = Number.parseInt(raw, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function CreateTenantDialog({
  open,
  onClose,
  onSuccess,
  client = defaultTenantsClient,
}: CreateTenantDialogProps) {
  // Core org
  const [orgName, setOrgName] = useState('');
  const [orgDomain, setOrgDomain] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState(DEFAULT_STATUS);

  // Entitlements (chips) + the pending entry being typed
  const [entitlements, setEntitlements] = useState<string[]>([]);
  const [entitlementDraft, setEntitlementDraft] = useState('');

  // Optional numeric limits (kept as strings for controlled inputs)
  const [seatLimit, setSeatLimit] = useState('');
  const [projectLimit, setProjectLimit] = useState('');

  // Admin user
  const [adminEmail, setAdminEmail] = useState('');
  const [adminFirstName, setAdminFirstName] = useState('');
  const [adminLastName, setAdminLastName] = useState('');
  const [emailTouched, setEmailTouched] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadOrgs = useOrgsStore((state) => state.loadOrgs);
  const setTargetOrg = useUsersStore((state) => state.setTargetOrg);

  // Reset the form each time the dialog transitions to open (React-recommended
  // previous-prop pattern, matching InviteUserDialog).
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) {
      setOrgName('');
      setOrgDomain('');
      setCustomerId('');
      setSubscriptionStatus(DEFAULT_STATUS);
      setEntitlements([]);
      setEntitlementDraft('');
      setSeatLimit('');
      setProjectLimit('');
      setAdminEmail('');
      setAdminFirstName('');
      setAdminLastName('');
      setEmailTouched(false);
      setError(null);
      setIsSubmitting(false);
    }
  }

  const emailIsValid = isValidEmail(adminEmail);
  const showEmailError =
    emailTouched && adminEmail.trim() !== '' && !emailIsValid;

  const requiredFilled =
    orgName.trim() !== '' &&
    orgDomain.trim() !== '' &&
    customerId.trim() !== '' &&
    adminFirstName.trim() !== '' &&
    adminLastName.trim() !== '' &&
    emailIsValid;

  const canSubmit = requiredFilled && !isSubmitting;

  const addEntitlement = useCallback(() => {
    const value = entitlementDraft.trim();
    if (value === '') return;
    setEntitlements((prev) => (prev.includes(value) ? prev : [...prev, value]));
    setEntitlementDraft('');
  }, [entitlementDraft]);

  const removeEntitlement = useCallback((value: string) => {
    setEntitlements((prev) => prev.filter((e) => e !== value));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setIsSubmitting(true);
    setError(null);

    const req: CreateTenantRequest = {
      orgName: orgName.trim(),
      orgDomain: orgDomain.trim(),
      customerId: customerId.trim(),
      subscriptionStatus,
      entitlements,
      seatLimit: toCount(seatLimit),
      projectLimit: toCount(projectLimit),
      adminEmail: adminEmail.trim(),
      adminFirstName: adminFirstName.trim(),
      adminLastName: adminLastName.trim(),
    };

    try {
      const { orgId } = await client.createTenant(req);
      // Refresh the picker list, then select the new tenant so the admin lands
      // in its context immediately.
      await loadOrgs();
      await setTargetOrg(orgId);
      onClose();
      onSuccess?.(
        `Tenant "${req.orgName}" created — the admin will receive an invitation email.`,
      );
    } catch (err) {
      // 409 = name/domain collision; everything else is a generic failure.
      if (isHttpError(err) && err.status === 409) {
        setError('A tenant with this name or domain already exists.');
      } else {
        setError(formatErrorMessage(err));
      }
      setIsSubmitting(false);
    }
  }, [
    canSubmit,
    orgName,
    orgDomain,
    customerId,
    subscriptionStatus,
    entitlements,
    seatLimit,
    projectLimit,
    adminEmail,
    adminFirstName,
    adminLastName,
    client,
    loadOrgs,
    setTargetOrg,
    onClose,
    onSuccess,
  ]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="create-tenant-dialog"
    >
      <DialogTitle data-testid="create-tenant-dialog-title">
        Create tenant
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
          {error && (
            <Alert severity="error" data-testid="create-tenant-error">
              {error}
            </Alert>
          )}

          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
            }}
          >
            Organization
          </Typography>
          <TextField
            label="Organization name"
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            disabled={isSubmitting}
            required
            fullWidth
            slotProps={{
              htmlInput: { 'data-testid': 'create-tenant-org-name' },
            }}
          />
          <TextField
            label="Primary domain"
            placeholder="acme.fi"
            value={orgDomain}
            onChange={(e) => setOrgDomain(e.target.value)}
            disabled={isSubmitting}
            required
            fullWidth
            slotProps={{
              htmlInput: { 'data-testid': 'create-tenant-org-domain' },
            }}
          />
          <TextField
            label="Customer ID"
            placeholder="cust_…"
            value={customerId}
            onChange={(e) => setCustomerId(e.target.value)}
            disabled={isSubmitting}
            required
            fullWidth
            slotProps={{
              htmlInput: { 'data-testid': 'create-tenant-customer-id' },
            }}
          />
          <TextField
            select
            label="Subscription status"
            value={subscriptionStatus}
            onChange={(e) => setSubscriptionStatus(e.target.value)}
            disabled={isSubmitting}
            fullWidth
            slotProps={{
              htmlInput: { 'data-testid': 'create-tenant-subscription' },
            }}
          >
            {SUBSCRIPTION_STATUSES.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>

          {/* Entitlements — add-on-enter chips */}
          <Box>
            <Stack
              direction="row"
              spacing={1}
              sx={{
                alignItems: 'flex-start',
              }}
            >
              <TextField
                label="Entitlement"
                placeholder="llm.platform_keys"
                value={entitlementDraft}
                onChange={(e) => setEntitlementDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addEntitlement();
                  }
                }}
                disabled={isSubmitting}
                fullWidth
                size="small"
                slotProps={{
                  htmlInput: {
                    'data-testid': 'create-tenant-entitlement-input',
                  },
                }}
              />
              <Button
                onClick={addEntitlement}
                disabled={isSubmitting || entitlementDraft.trim() === ''}
                data-testid="create-tenant-entitlement-add"
                sx={{ mt: 0.5 }}
              >
                Add
              </Button>
            </Stack>
            {entitlements.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                useFlexGap
                sx={{
                  flexWrap: 'wrap',
                  mt: 1,
                }}
              >
                {entitlements.map((e) => (
                  <Chip
                    key={e}
                    label={e}
                    onDelete={
                      isSubmitting ? undefined : () => removeEntitlement(e)
                    }
                    data-testid={`create-tenant-entitlement-chip-${e}`}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* Optional numeric limits (0 / blank = no limit) */}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Seat limit"
              type="number"
              value={seatLimit}
              onChange={(e) => setSeatLimit(e.target.value)}
              disabled={isSubmitting}
              fullWidth
              slotProps={{
                htmlInput: {
                  min: 0,
                  'data-testid': 'create-tenant-seat-limit',
                },
              }}
            />
            <TextField
              label="Project limit"
              type="number"
              value={projectLimit}
              onChange={(e) => setProjectLimit(e.target.value)}
              disabled={isSubmitting}
              fullWidth
              slotProps={{
                htmlInput: {
                  min: 0,
                  'data-testid': 'create-tenant-project-limit',
                },
              }}
            />
          </Stack>

          <Typography
            variant="overline"
            sx={{
              color: 'text.secondary',
            }}
          >
            First admin
          </Typography>
          <TextField
            label="Admin email"
            type="email"
            value={adminEmail}
            onChange={(e) => setAdminEmail(e.target.value)}
            onBlur={() => setEmailTouched(true)}
            disabled={isSubmitting}
            error={showEmailError}
            helperText={
              showEmailError ? 'Please enter a valid email address' : ''
            }
            required
            fullWidth
            slotProps={{
              htmlInput: { 'data-testid': 'create-tenant-admin-email' },
            }}
          />
          <Stack direction="row" spacing={2}>
            <TextField
              label="First name"
              value={adminFirstName}
              onChange={(e) => setAdminFirstName(e.target.value)}
              disabled={isSubmitting}
              required
              fullWidth
              slotProps={{
                htmlInput: {
                  maxLength: 200,
                  'data-testid': 'create-tenant-admin-first',
                },
              }}
            />
            <TextField
              label="Last name"
              value={adminLastName}
              onChange={(e) => setAdminLastName(e.target.value)}
              disabled={isSubmitting}
              required
              fullWidth
              slotProps={{
                htmlInput: {
                  maxLength: 200,
                  'data-testid': 'create-tenant-admin-last',
                },
              }}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          data-testid="create-tenant-cancel"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          data-testid="create-tenant-submit"
        >
          {isSubmitting ? 'Creating…' : 'Create tenant'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
